import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import { StudioLayout } from '@/shared/layout/studio-layout';
import { Alert } from '@/shared/ui/alert';
import { buttonVariants } from '@/shared/ui/button-variants';
import { PageSkeleton } from '@/shared/ui/skeleton';

import { usePlacement, useSubmitPlacement } from '../api/use-placement';
import { PlacementForm } from '../components/placement-form';

import '@/shared/ui/curriculum/curriculum.css';
import '../placement.css';

/** "Change my path": the questionnaire inside Studio, so explorer labels update as the path is saved. */
export function PlacementPage() {
  const placement = usePlacement();
  const submit = useSubmitPlacement();
  const navigate = useNavigate();
  return (
    <StudioLayout activeId={null} subtitle="Your path" scrollKey="placement">
      {(studio) => {
        const saved = placement.data?.placement ?? null;
        return (
          <div className="studio-page path-page">
            <header>
              <div className="path-location">
                <Link to="/studio" aria-label="Back to Studio">
                  <ArrowLeft size={14} aria-hidden="true" /> Studio
                </Link>
                <span className="path-location-divider" aria-hidden="true">
                  /
                </span>
                <span>Your path</span>
              </div>
              <h1>{saved ? 'Your path' : 'Choose where to start'}</h1>
              <p className="path-intro">
                Four modules build one application, and each stands on its own. Your answers set
                where you start and how modules are labelled in the curriculum. Every module stays
                open to you.
              </p>
            </header>
            {placement.error ? (
              <Alert>We could not load your saved answers. {placement.error.message}</Alert>
            ) : null}
            {placement.isPending ? (
              <PageSkeleton label="Loading your path…" />
            ) : (
              <PlacementForm
                key={saved?.updatedAt ?? 'new'}
                modules={studio.modules.map((module) => ({
                  number: module.number,
                  title: module.title,
                  placement: module.placement,
                }))}
                initialAnswers={saved?.answers}
                initialPath={saved?.path}
                savedAt={saved?.updatedAt}
                saving={submit.isPending}
                error={submit.error?.message ?? null}
                summaryActions={(startModule) => (
                  <Link
                    to="/studio/modules/$number"
                    params={{ number: String(startModule) }}
                    className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                  >
                    Open Module {startModule}{' '}
                    <ArrowRight size={13} strokeWidth={1.75} aria-hidden="true" />
                  </Link>
                )}
                onSave={(input) =>
                  submit.mutate(input, {
                    onSuccess: (result) =>
                      void navigate({
                        to: '/studio/modules/$number',
                        params: { number: String(result.placement.path.startModule) },
                      }),
                  })
                }
              />
            )}
          </div>
        );
      }}
    </StudioLayout>
  );
}
